/**
 * Quali file si allegano a un'email (decisione di Davide del 3/11/2026, dopo la segnalazione di Carmem: «quando
 * caricano un allegato questo non va insieme alla email»).
 *
 * Fino alla 1.35.0 la posta usava l'elenco dei documenti dell'archivio (MIME_AMMESSI in servizi/archivio.ts: PDF,
 * alcune immagini, Word, Excel, ODT/ODS, testo, .eml). Restavano fuori .p7m, l'XML delle fatture, .zip, .pptx,
 * .msg, .rtf, .gif, audio, video e i file a cui il browser non da' un tipo. Ora vale la regola di Gmail e di
 * Roundcube: si allega tutto, tranne i programmi. L'elenco dell'archivio documenti non cambia.
 *
 * Il rifiuto guarda l'estensione finale del nome, anche dopo un'altra («fattura.pdf.exe»), senza i punti e gli
 * spazi in coda che Windows toglie («fattura.exe.») e senza i caratteri che invertono la scrittura, e il tipo
 * MIME dei programmi. Lo usano l'API (prenotazione, invio, bozza condivisa, inoltro) e il compositore, che
 * avvisa prima di caricare.
 *
 * Gli archivi .zip e .7z si ammettono: l'antivirus del server di posta li apre, all'invio e all'arrivo. Uno .zip
 * cifrato con una password non si apre: il compositore lo dice (`zipCifrato`), senza fermarlo.
 */

/**
 * Programmi ed estensioni che eseguono codice: quelle bloccate da Gmail (support.google.com/mail/answer/6590,
 * «File types blocked in Gmail») e quelle chieste da CIDIS (reg, inf, scf, app, gadget, application, vhdx, ws,
 * psm1).
 */
export const ESTENSIONI_BLOCCATE: ReadonlySet<string> = new Set([
  // CIDIS
  'exe', 'com', 'bat', 'cmd', 'scr', 'pif', 'cpl', 'msi', 'msp', 'mst', 'jar', 'js', 'jse', 'vbs', 'vbe', 'wsf',
  'wsh', 'ws', 'ps1', 'psm1', 'hta', 'lnk', 'reg', 'inf', 'scf', 'dll', 'sys', 'app', 'apk', 'dmg', 'iso', 'img',
  'vhd', 'vhdx', 'gadget', 'application', 'appx', 'msix',
  // Gmail, in piu'
  'ade', 'adp', 'appxbundle', 'cab', 'chm', 'diagcab', 'diagcfg', 'diagpkg', 'ex', 'ex_', 'ins', 'isp', 'jnlp',
  'lib', 'mde', 'mjs', 'msc', 'msixbundle', 'nsh', 'sct', 'shb', 'vb', 'vxd', 'wsc', 'xll',
])

/** Tipi MIME dei programmi: un file senza estensione nota ma dichiarato eseguibile resta fuori. */
export const MIME_BLOCCATI: ReadonlySet<string> = new Set([
  'application/x-msdownload', 'application/x-msdos-program', 'application/x-dosexec', 'application/x-executable',
  'application/vnd.microsoft.portable-executable', 'application/x-ms-installer', 'application/x-msi',
  'application/x-ms-shortcut', 'application/x-ms-application', 'application/hta', 'application/x-bat',
  'application/java-archive', 'application/x-java-archive', 'application/x-java-jnlp-file',
  'application/vnd.android.package-archive', 'application/x-apple-diskimage', 'application/x-iso9660-image',
  'application/javascript', 'application/x-javascript', 'text/javascript', 'text/vbscript', 'application/x-vbscript',
  'application/x-ms-wim', 'application/vnd.ms-cab-compressed', 'application/x-msdos-windows',
])

/** Caratteri che invertono o isolano la scrittura (U+200E, U+200F, U+202A-U+202E, U+2066-U+2069). */
const DIREZIONE = /[‎‏‪-‮⁦-⁩]/g

/** L'estensione finale, in minuscolo, senza punti e spazi in coda; '' se non c'e'. */
export function estensioneDi(nome: string): string {
  const pulito = nome.normalize('NFKC').replace(DIREZIONE, '').replace(/[\s. ]+$/u, '')
  // Solo il nome, senza un eventuale percorso.
  const base = pulito.slice(pulito.search(/[^\\/]*$/))
  const punto = base.lastIndexOf('.')
  return punto > 0 || (punto === 0 && base.length > 1) ? base.slice(punto + 1).toLowerCase() : ''
}

export type EsitoAllegato = { ammesso: true } | { ammesso: false; motivo: string }

/** Il nome come si scrive nei messaggi: senza i caratteri di direzione, che lo farebbero leggere al contrario. */
const nomeLeggibile = (nome: string) => nome.replace(DIREZIONE, '').trim() || 'il file'

/** Il testo del rifiuto, uguale nel compositore e nell'API. */
export const motivoProgramma = (nome: string) =>
  `«${nomeLeggibile(nome)}» non si può allegare: i programmi non si spediscono per posta.`

/** Si puo' allegare a un'email? Tutto tranne i programmi, per estensione o per tipo. */
export function allegatoPostaAmmesso(nome: string, mime: string | null | undefined): EsitoAllegato {
  const tipo = (mime ?? '').split(';')[0]!.trim().toLowerCase()
  if (ESTENSIONI_BLOCCATE.has(estensioneDi(nome)) || MIME_BLOCCATI.has(tipo)) {
    return { ammesso: false, motivo: motivoProgramma(nome) }
  }
  return { ammesso: true }
}

/** Il tipo da dichiarare per un file: quello del browser, o application/octet-stream se non ne da' uno. */
export function tipoDichiarato(mime: string | null | undefined): string {
  const tipo = (mime ?? '').trim()
  return tipo && tipo.length <= 120 ? tipo : 'application/octet-stream'
}

/**
 * Uno .zip cifrato con una password, dai primi 30 byte: l'intestazione locale del primo file (PK\x03\x04) ha il
 * bit 0 dei flag acceso. Lo .7z cifrato non si riconosce dall'inizio del file: non si segnala.
 */
export function zipCifrato(primiByte: Uint8Array): boolean {
  if (primiByte.length < 8) return false
  const firma = primiByte[0] === 0x50 && primiByte[1] === 0x4b && primiByte[2] === 0x03 && primiByte[3] === 0x04
  return firma && (primiByte[6]! & 0x01) === 0x01
}
